import React, { useState, useEffect, useRef } from 'react';
// import { products as allProducts } from '../mock/products'; // Eliminar este import
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, Label, Sector } from 'recharts';
import { supabase } from '../utils/supabase';
import { obtenerDolarAutomatico, formatearDolar } from '../utils/dolarUtils';
import { getArgentinaISOString } from '../utils/timezoneUtils';
import MarginsModal from './modals/MarginsModal';
import PendingImagesModal from './modals/PendingImagesModal';
import ImageModal from './modals/ImageModal';
import MagicWandModal from './MagicWandModal';


const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28CFF', '#FF6699', '#FFB347', '#B6FFB3'];

// Hook para detectar si es móvil
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

const darkenColor = (hex, amount = 0.5) => {
  // Convierte el color hex a RGB y lo oscurece
  let col = hex.replace('#', '');
  if (col.length === 3) col = col.split('').map(x => x + x).join('');
  const num = parseInt(col, 16);
  let r = (num >> 16) & 0xFF;
  let g = (num >> 8) & 0xFF;
  let b = num & 0xFF;
  r = Math.floor(r * (1 - amount));
  g = Math.floor(g * (1 - amount));
  b = Math.floor(b * (1 - amount));
  return `rgb(${r},${g},${b})`;
};

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('ventas'); // 'ventas' o 'historial' o 'productos'
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    netProfit: 0,
    topProducts: []
  });
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [serialEdit, setSerialEdit] = useState({ orderId: null, itemIndex: null, value: '', viewOnly: true });
  const isMobile = useIsMobile();
  const [openProductsDropdown, setOpenProductsDropdown] = useState(null);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [productsModalOrder, setProductsModalOrder] = useState(null);
  const [allProducts, setAllProducts] = useState([]); // Nuevo estado para productos
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedProductForImage, setSelectedProductForImage] = useState(null);
  // Estado para el filtro de búsqueda
  const [productSearch, setProductSearch] = useState("");
  // Estado para alternar entre productos visibles y ocultos
  const [showVisibleProducts, setShowVisibleProducts] = useState(true);
  // Estado para controlar la animación de desvanecimiento
  const [fadingProducts, setFadingProducts] = useState(new Set());
  // Estado para edición de precio
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [editingPriceValue, setEditingPriceValue] = useState("");
  // Estado para filtro de cliente en órdenes
  const [orderClientSearch, setOrderClientSearch] = useState("");
  // Estado para el dólar
  const [dolar, setDolar] = useState('');
  const [guardandoDolar, setGuardandoDolar] = useState(false);
  const [mensajeDolar, setMensajeDolar] = useState('');

  // Agregar nuevos estados para las listas
  const [celularesLista, setCelularesLista] = useState("");
  const [accesoriosLista, setAccesoriosLista] = useState("");
  const [mensajeCelulares, setMensajeCelulares] = useState("");
  const [mensajeAccesorios, setMensajeAccesorios] = useState("");
  const [cargandoCelulares, setCargandoCelulares] = useState(false);
  const [cargandoAccesorios, setCargandoAccesorios] = useState(false);

  // Leer el dólar actual al montar y actualizar automáticamente
  useEffect(() => {
    const fetchDolar = async () => {
      const { data, error } = await supabase
        .from('dolar')
        .select('*')
        .order('id', { ascending: false })
        .limit(1);
      if (!error && data && data.length > 0) {
        setDolar(data[0].valor);
      }
    };

    const inicializarDolar = async () => {
      // Primero cargar el valor actual de la base de datos
      await fetchDolar();

      // Luego actualizar automáticamente con la API
      console.log('🔄 Actualizando dólar al cargar la página...');
      try {
        const valorDolar = await obtenerDolarAutomatico();

        if (valorDolar && valorDolar > 0) {
          await actualizarDolarEnBD(valorDolar, 'Actualización al Cargar Página');
          console.log('✅ Dólar actualizado al cargar la página:', valorDolar);
        } else {
          console.log('⚠️ No se pudo obtener el dólar al cargar la página');
        }
      } catch (error) {
        console.error('❌ Error actualizando dólar al cargar la página:', error);
      }
    };

    inicializarDolar();
  }, []);

  // Función para obtener el dólar usando dolarUtils.js
  const obtenerDolarSantander = async () => {
    setGuardandoDolar(true);

    try {
      const valorDolar = await obtenerDolarAutomatico();

      if (valorDolar && valorDolar > 0) {
        await actualizarDolarEnBD(valorDolar, 'API Automática');
      } else {
        // Si no se pudo obtener el valor, usar un valor estimado
        const valorEstimado = dolar * 1.02; // Incremento del 2% como estimación
        await actualizarDolarEnBD(valorEstimado, 'Estimación');
      }
    } catch (error) {
      console.error('Error obteniendo cotización:', error);

      // En caso de error total, usar un valor estimado
      const valorEstimado = dolar * 1.02; // Incremento del 2% como estimación
      await actualizarDolarEnBD(valorEstimado, 'Estimación por Error');
    } finally {
      setGuardandoDolar(false);
      setTimeout(() => { setMensajeDolar(''); }, 4000);
    }
  };

  // Actualización automática del dólar cada media hora entre 12:00 y 17:30
  useEffect(() => {
    const actualizarDolarAutomatico = async () => {
      const ahora = new Date();
      const hora = ahora.getHours();
      const minutos = ahora.getMinutes();

      // Verificar si estamos en el horario permitido (12:00 - 17:30)
      if (hora >= 12 && hora < 17 || (hora === 17 && minutos <= 30)) {
        console.log('🔄 Actualización automática del dólar iniciada...');
        try {
          const valorDolar = await obtenerDolarAutomatico();

          if (valorDolar && valorDolar > 0) {
            await actualizarDolarEnBD(valorDolar, 'Actualización Automática');
            console.log('✅ Dólar actualizado automáticamente:', valorDolar);
          } else {
            console.log('⚠️ No se pudo obtener el dólar automáticamente');
          }
        } catch (error) {
          console.error('❌ Error en actualización automática del dólar:', error);
        }
      }
    };

    // Ejecutar inmediatamente si estamos en el horario permitido
    actualizarDolarAutomatico();

    // Configurar el intervalo para ejecutar cada 30 minutos (1800000 ms)
    const intervalo = setInterval(actualizarDolarAutomatico, 1800000);

    // Limpiar el intervalo cuando el componente se desmonte
    return () => clearInterval(intervalo);
  }, [dolar]); // Dependencia en dolar para que se actualice cuando cambie el valor

  // Función auxiliar para actualizar el dólar en la base de datos
  const actualizarDolarEnBD = async (valorNum, fuente) => {
    try {
      // Aumentar 10 pesos a la cotización recibida por la API
      const valorConAjuste = valorNum + 10;
      const timestampActual = getArgentinaISOString();

      // Obtener el id de la fila actual (la de mayor id)
      const { data: filas, error: errorSelect } = await supabase
        .from('dolar')
        .select('id')
        .order('id', { ascending: false })
        .limit(1);

      if (!errorSelect && filas && filas.length > 0) {
        const idActual = filas[0].id;
        console.log('Actualizando valor en id:', idActual, 'con valor original:', valorNum, 'y valor ajustado:', valorConAjuste, 'fecha:', timestampActual);
        const { error } = await supabase
          .from('dolar')
          .update({
            valor: valorConAjuste,
            updated_at: timestampActual
          })
          .eq('id', idActual);
        if (!error) {
          setDolar(valorConAjuste);
          // No mostrar mensaje para actualizaciones automáticas
          if (fuente !== 'API Automática' && fuente !== 'Actualización Automática' && fuente !== 'Actualización al Cargar Página') {
            setMensajeDolar(`¡Dólar actualizado desde ${fuente}! ($${valorNum.toFixed(2)} + $10 = $${valorConAjuste.toFixed(2)})`);
          }
          console.log('✅ Dólar actualizado exitosamente en la base de datos');
        } else {
          setMensajeDolar('Error al guardar en base de datos');
          console.error('Error al actualizar valor:', error);
        }
      } else {
        setMensajeDolar('No se encontró la fila de dólar');
        console.error('No se encontró la fila de dólar para actualizar');
      }
    } catch (error) {
      console.error('Error actualizando dólar en BD:', error);
      setMensajeDolar('Error al guardar en base de datos');
    }
  };

  // Función para mostrar el porcentaje dentro de cada porción
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    // Centrar exactamente entre innerRadius y outerRadius
    const radius = innerRadius + (outerRadius - innerRadius) / 2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const color = darkenColor(COLORS[index % COLORS.length], 0.4);
    return (
      <text x={x} y={y} fill={color} textAnchor="middle" dominantBaseline="central" fontSize={isMobile ? 10 : 14} fontWeight="bold">
        {percent > 0 ? `${(percent * 100).toFixed(0)}%` : ''}
      </text>
    );
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        console.log('Intentando conectar a Supabase...');
        console.log('URL:', supabase.supabaseUrl);

        const { data, error } = await supabase
          .from('orders')
          .select('*')
          .order('date', { ascending: false });

        if (error) {
          console.error('Error de Supabase:', error);
          return;
        }

        console.log('Pedidos cargados exitosamente:', data);
        setOrders(data || []);
        calculateStats(data || []); // Calcular estadísticas con todas las órdenes
      } catch (err) {
        console.error('Error inesperado:', err);
      }
    };
    fetchOrders();
  }, []);

  // Cargar productos desde Supabase al entrar a la pestaña 'productos'
  useEffect(() => {
    if (activeTab === 'productos') {
      const fetchProducts = async () => {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .order('name', { ascending: true });
        if (error) {
          console.error('Error cargando productos:', error);
        } else {
          console.log('📦 Productos cargados:', data?.length || 0);
          // Verificar si hay productos con imágenes
          const productosConImagen = data?.filter(p => p.image && p.image !== 'blank.jpg' && p.image !== '') || [];
          console.log('🖼️ Productos con imagen:', productosConImagen.length);
          if (productosConImagen.length > 0) {
            console.log('📸 Ejemplo de producto con imagen:', productosConImagen[0]);
          }
          setAllProducts(data || []);
        }
      };
      fetchProducts();
    }
  }, [activeTab]);

  // Buscar el precio proveedor en USD por nombre de producto (sin color)
  const getProviderPriceUSD = (productName) => {
    // Quitar color del nombre (siempre es ' - Color' al final)
    const baseName = productName.split(' - ')[0];
    // Buscar por nombre base
    const found = allProducts.find(p => p.name === baseName);
    if (found) {
      if (found.costUSD !== undefined) {
        return found.costUSD;
      }
      if (found["Precio Proveedor (USD)"]) {
        return found["Precio Proveedor (USD)"];
      }
    }
    return 0;
  };

  const calculateStats = (ordersList) => {
    // Incluir todas las órdenes en los cálculos, sin filtrar por archived
    const totalOrders = ordersList.length;
    const totalRevenue = ordersList.reduce((sum, order) => sum + order.total, 0);
    let netProfit = 0;

    ordersList.forEach(order => {
      order.items.forEach(item => {
        const pricears = item.pricears || 0;
        const priceusd = item.priceusd !== undefined ? item.priceusd : 0;
        const dolarusado = order.dolarusado || order.dolarUsado || order.dolar_usado || order.dolar || 0;
        const quantity = item.quantity || 1;
        const gananciaNeta = (pricears - (priceusd * dolarusado)) * quantity;
        netProfit += gananciaNeta;
      });
    });

    // Agrupar productos por MARCA
    const brandSales = {};
    ordersList.forEach(order => {
      order.items.forEach(item => {
        const brand = item.brand || 'Sin marca';
        brandSales[brand] = (brandSales[brand] || 0) + item.quantity;
      });
    });

    const topProducts = Object.entries(brandSales)
      .sort(([, a], [, b]) => b - a)
      .map(([name, quantity]) => ({ name, quantity }));

    // Calcular el total de unidades vendidas
    const totalUnitsSold = ordersList.reduce(
      (sum, order) => sum + order.items.reduce((s, item) => s + item.quantity, 0),
      0
    );

    setStats({
      totalOrders,
      totalRevenue,
      netProfit,
      topProducts,
      totalUnitsSold
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    onLogout();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Argentina/Buenos_Aires' // Forzar horario de Buenos Aires
    });
  };

  // Alternar estado pagado/pending
  const handleTogglePaid = (orderId) => {
    const updatedOrders = orders.map(order => {
      if (order.id === orderId) {
        return {
          ...order,
          status: order.status === 'paid' ? 'pending' : 'paid'
        };
      }
      return order;
    });
    setOrders(updatedOrders);
    localStorage.setItem('orders', JSON.stringify(updatedOrders));
    calculateStats(updatedOrders); // Ya incluye todas las órdenes
  };

  // Cancelar orden
  const handleCancelOrder = (orderId) => {
    if (!window.confirm('¿Seguro que deseas cancelar esta compra?')) return;
    const updatedOrders = orders.filter(order => order.id !== orderId);
    setOrders(updatedOrders);
    localStorage.setItem('orders', JSON.stringify(updatedOrders));
    calculateStats(updatedOrders); // Ya incluye todas las órdenes
  };

  const calcularGananciaOrden = (order) => {
    let gananciaTotal = 0;
    order.items.forEach(item => {
      // Nueva fórmula: pricears - (priceusd * dolarusado)
      const pricears = item.pricears || 0;
      const priceusd = item.priceusd !== undefined ? item.priceusd : 0;
      const dolarusado = order.dolarusado || order.dolarUsado || order.dolar_usado || order.dolar || 0;
      const quantity = item.quantity || 1;
      const gananciaNeta = (pricears - (priceusd * dolarusado)) * quantity;
      console.log('DEBUG GANANCIA (calcularGananciaOrden):', {
        pricears,
        priceusd,
        dolarusado,
        quantity,
        gananciaNeta
      });
      gananciaTotal += gananciaNeta;
    });
    return gananciaTotal;
  };

  const handleArchiveOrder = async (orderId) => {
    // Actualizar en Supabase
    const { error } = await supabase
      .from('orders')
      .update({ archived: true })
      .eq('id', orderId);
    if (error) {
      return;
    }
    // Actualizar en el frontend
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, archived: true } : o));
  };

  const handleUnarchiveOrder = async (orderId) => {
    const { error } = await supabase
      .from('orders')
      .update({ archived: false })
      .eq('id', orderId);
    if (error) {
      return;
    }
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, archived: false } : o));
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .delete()
        .eq('id', Number(orderId));

      if (error) {
        console.error('Error eliminando la orden:', error);
        alert('Error al eliminar la orden');
        return;
      }

      setOrders(prev => {
        const newOrders = prev.filter(o => Number(o.id) !== Number(orderId));
        calculateStats(newOrders);
        return newOrders;
      });
    } catch (e) {
      console.error('Error inesperado:', e);
      alert('Error al eliminar la orden');
    }
  };

  // Filtrar órdenes según la pestaña activa
  const filteredOrders = activeTab === 'ventas'
    ? orders.filter(o => !o.archived)
    : orders.filter(o => o.archived);

  // Guardar número de serie en Supabase
  const handleSaveSerial = async () => {
    const { orderId, itemIndex, value } = serialEdit;
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    const newItems = [...order.items];
    newItems[itemIndex] = { ...newItems[itemIndex], serialNumber: value };

    // LOGS DE DEPURACIÓN
    console.log('Intentando actualizar serial en Supabase');
    console.log('orderId:', orderId, typeof orderId);
    console.log('newItems:', newItems);

    const { data, error } = await supabase
      .from('orders')
      .update({ items: newItems })
      .eq('id', Number(orderId)); // Forzar a número

    if (error) {
      alert('Error guardando número de serie: ' + error.message);
      console.error('Supabase error:', error);
      return;
    }
    if (data) {
      console.log('Respuesta de Supabase:', data);
    }
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, items: newItems } : o));
    setShowSerialModal(false);
  };

  // Función para alternar la visibilidad de un producto
  const handleToggleProductVisible = async (productId) => {
    const product = allProducts.find(p => p.id === productId);
    if (!product) return;

    // Agregar el producto al estado de desvanecimiento
    setFadingProducts(prev => new Set([...prev, productId]));

    // Esperar 300ms para que se vea la animación de desvanecimiento
    setTimeout(async () => {
      const updatedProducts = allProducts.map(p => {
        if (p.id === productId) {
          return {
            ...p,
            visible: !p.visible
          };
        }
        return p;
      });
      setAllProducts(updatedProducts); // Actualizar el estado local

      // Guardar en Supabase
      const { error } = await supabase
        .from('products')
        .update({ visible: !product.visible })
        .eq('id', productId);

      if (error) {
        alert('Error actualizando visibilidad del producto: ' + error.message);
        console.error('Supabase error al actualizar visibilidad:', error);
        // Revertir el estado local si la actualización en Supabase falla
        setAllProducts(prev => prev.map(p => p.id === productId ? { ...p, visible: !product.visible } : p));
      } else {
        console.log('Visibilidad del producto actualizada en Supabase:', updatedProducts);
      }

      // Remover del estado de desvanecimiento
      setFadingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }, 300);
  };

  // Función para guardar el precio editado
  const handleSavePriceUSD = async (productId) => {
    const newPrice = parseFloat(editingPriceValue);
    if (isNaN(newPrice) || newPrice <= 0) return;
    // Actualizar en Supabase
    const { error } = await supabase
      .from('products')
      .update({ price: newPrice })
      .eq('id', productId);
    if (!error) {
      setAllProducts(prev => prev.map(p => p.id === productId ? { ...p, price: newPrice } : p));
      setEditingPriceId(null);
    } else {
      alert('Error actualizando precio: ' + error.message);
    }
  };

  const handleOpenImageModal = (product) => {
    setSelectedProductForImage(product);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedProductForImage(null);
  };

  const [showMagicWandModal, setShowMagicWandModal] = useState(false);
  const [selectedProductForMagicWand, setSelectedProductForMagicWand] = useState(null);
  const [notification, setNotification] = useState(null);

  const handleOpenMagicWandModal = (product) => {
    setSelectedProductForMagicWand(product);
    setShowMagicWandModal(true);
  };

  const handleCloseMagicWandModal = () => {
    setShowMagicWandModal(false);
    setSelectedProductForMagicWand(null);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleImageUpdate = (productId, newImageName) => {
    // Actualizar el estado local
    setAllProducts(prev => prev.map(p =>
      p.id === productId ? { ...p, image: newImageName } : p
    ));
  };

  const dolarButtonRef = useRef(null);

  // Función para procesar la lista de celulares con categorización automática
  const procesarListaCelulares = async () => {
    try {
      setCargandoCelulares(true);
      setMensajeCelulares("Procesando lista de celulares...");

      // Obtener valor actual del dólar
      const { data: dolarData } = await supabase.from('dolar').select('valor').single();
      const dolarValue = dolarData?.valor || 1;

      // Obtener productos existentes de todas las categorías de celulares
      const { data: existingProducts, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .in('categoria', ['iPhone', 'Xiaomi', 'Samsung', 'Motorola']);

      if (fetchError) throw fetchError;

      // Normalizar la lista
      const listaNomalizada = celularesLista
        .replace(/\u00A0/g, ' ')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n');

      const lineasCelulares = listaNomalizada.split('\n').filter(line => line.trim());
      const productosNuevos = [];
      const productosParaActualizar = [];
      const productosSinStockIds = [];
      const productosQuitadosDeListaIds = [];

      // Crear mapa de productos existentes
      const existingProductsMap = new Map(
        existingProducts.map(p => [p.name.toLowerCase(), p])
      );

      // Función para normalizar el nombre de categoría al formato de la BD
      const normalizarCategoria = (cat) => {
        const catLower = cat.toLowerCase();
        if (catLower === 'iphone') return 'iPhone';
        if (catLower === 'xiaomi') return 'Xiaomi';
        if (catLower === 'samsung') return 'Samsung';
        if (catLower === 'motorola' || catLower === 'moto') return 'Motorola';
        return cat;
      };

      // Set con nombres de la nueva lista
      const nombresEnNuevaLista = new Set();
      // Set para evitar duplicados dentro de la misma lista pegada
      const nombresVistosEnLista = new Set();

      let categoriaActual = 'Celulares';
      let i = 0;
      
      while (i < lineasCelulares.length) {
        const linea = lineasCelulares[i].trim();
        
        if (!linea) {
          i++;
          continue;
        }

        // Detectar categoría con * asteriscos (FUENTE PRINCIPAL de la categoría)
        const esCategoriaAsteriscos = linea.startsWith('*') && linea.endsWith('*');
        if (esCategoriaAsteriscos) {
          const cat = linea.replace(/\*/g, '').trim();
          categoriaActual = normalizarCategoria(cat);
          console.log('🏷️ Categoría detectada (asteriscos):', categoriaActual);
          i++;
          continue;
        }

        // NO detectar categorías sin asteriscos - usar la última categoría de asteriscos
        // Esto evita que nombres de productos se confundan con categorías

        // Procesar producto
        let nombre = '';
        let precioUSD = 0;
        let esSinStock = false;
        const tieneTab = linea.includes('\t');

        if (tieneTab) {
          // Formato con tab: Nombre[TAB]Precio
          const partes = linea.split('\t').map(item => item.trim());
          nombre = partes[0];
          const precioStr = partes[1] || '';
          
          if (nombre && precioStr) {
            esSinStock = precioStr.toLowerCase() === 'sin stock';
            if (!esSinStock) {
              const precioLimpio = precioStr.replace(/[$ \t,\u00A0]/g, '').trim();
              precioUSD = parseFloat(precioLimpio) || 0;
            }
          }
        } else {
          // Formato sin tab: Nombre en línea actual, precio en siguiente línea
          const tieneSignoPeso = linea.includes('$');
          esSinStock = linea.toLowerCase().trim() === 'sin stock';
          
          if (tieneSignoPeso || esSinStock) {
            // Esta línea es precio, línea anterior es nombre
            if (i > 0) {
              const lineaAnterior = lineasCelulares[i - 1];
              const esAnteriorPrecio = lineaAnterior.includes('$') || lineaAnterior.toLowerCase().trim() === 'sin stock';
              
              if (!esAnteriorPrecio) {
                nombre = lineaAnterior;
                if (!esSinStock) {
                  const precioLimpio = linea.replace(/[$ \t,\u00A0]/g, '').trim();
                  precioUSD = parseFloat(precioLimpio) || 0;
                }
              }
            }
          }
        }

        if (nombre) {
          nombresEnNuevaLista.add(nombre.toLowerCase());
          console.log('✅ Producto:', nombre, '| Categoría:', categoriaActual, '| Precio USD:', precioUSD);

          // Verificar si ya vimos este nombre en esta misma lista (evitar duplicados)
          if (nombresVistosEnLista.has(nombre.toLowerCase())) {
            console.log(`⚠️ Producto duplicado en la lista, ignorando: "${nombre}"`);
            i++;
            continue;
          }
          nombresVistosEnLista.add(nombre.toLowerCase());

          if (esSinStock) {
            const existingProduct = existingProductsMap.get(nombre.toLowerCase());
            if (existingProduct) {
              productosSinStockIds.push(existingProduct.id);
            }
          } else if (precioUSD > 0) {
            const existingProduct = existingProductsMap.get(nombre.toLowerCase());
            // Usar la categoría de los asteriscos como fuente principal
            const categoria = categoriaActual;
            
            if (existingProduct) {
              // Producto existe - actualizar precio siempre
              productosParaActualizar.push({
                ...existingProduct,
                name: nombre,
                categoria: categoria,
                priceusd: precioUSD,
                price: precioUSD,
                pricears: Math.round(precioUSD * dolarValue),
                stock: true,
                visible: true,
                last_price_update: new Date().toISOString()
              });
            } else {
              // Producto nuevo - agregar a lista (sin id, se insertará por separado)
              productosNuevos.push({
                name: nombre,
                categoria: categoria,
                priceusd: precioUSD,
                price: precioUSD,
                pricears: Math.round(precioUSD * dolarValue),
                stock: true,
                visible: true,
                image: 'blank.jpg',
                margin_applied: 0,
                last_price_update: new Date().toISOString()
              });
            }
          }
        }

        i++;
      }

      // Productos que estaban en la BD pero ya no están en la nueva lista
      existingProducts.forEach(producto => {
        if (!nombresEnNuevaLista.has(producto.name.toLowerCase())) {
          productosQuitadosDeListaIds.push(producto.id);
        }
      });

      // Insertar productos nuevos (sin id para que se genere automáticamente)
      if (productosNuevos.length > 0) {
        const { error: insertError } = await supabase
          .from('products')
          .insert(productosNuevos);
        if (insertError) throw insertError;
      }

      // Actualizar productos existentes (ya tienen id)
      if (productosParaActualizar.length > 0) {
        const { error: updateError } = await supabase
          .from('products')
          .upsert(productosParaActualizar);
        if (updateError) throw updateError;
      }

      // Marcar productos sin stock como no visibles
      const todosSinStock = [...productosSinStockIds, ...productosQuitadosDeListaIds];
      if (todosSinStock.length > 0) {
        const { error: stockError } = await supabase
          .from('products')
          .update({ stock: false, visible: false })
          .in('id', todosSinStock);
        if (stockError) throw stockError;
      }

      // Mensaje resumen
      let mensajeResumen = '';
      if (productosNuevos.length > 0) {
        mensajeResumen += `\n✨ Productos nuevos agregados: ${productosNuevos.length}`;
      }
      if (productosParaActualizar.length > 0) {
        mensajeResumen += `\n🔄 Productos actualizados: ${productosParaActualizar.length}`;
      }
      if (todosSinStock.length > 0) {
        mensajeResumen += `\n❌ Productos marcados sin stock: ${todosSinStock.length}`;
      }

      setCelularesLista("");
      setMensajeCelulares(`Lista procesada exitosamente.${mensajeResumen}`);
    } catch (error) {
      console.error('Error:', error);
      setMensajeCelulares(`Error al procesar la lista: ${error.message}`);
    } finally {
      setCargandoCelulares(false);
    }
  };

  // Función para procesar la lista de accesorios con categorización automática
  const procesarListaAccesorios = async () => {
    console.log('🚀 Iniciando procesarListaAccesorios');
    try {
      setCargandoAccesorios(true);
      setMensajeAccesorios("Procesando lista de accesorios...");
      console.log('📝 Lista a procesar:', accesoriosLista.substring(0, 200) + '...');

      // Obtener valor actual del dólar
      const { data: dolarData } = await supabase.from('dolar').select('valor').single();
      const dolarValue = dolarData?.valor || 1;

      // Obtener productos existentes de todas las categorías de accesorios
      const { data: existingProducts, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .in('categoria', ['ACCESORIOS CELUS', 'ECOSISTEMA APPLE', 'NOTEBOOKS', 'TABLETS', 'AUDIO-PARLANTES', 'AUDIO-AURIS', 'AUDIO-BARRAS DE SONIDO', 'SMARTWATCHES', 'ELECTRONICA LIVIANA', 'CONSOLAS/GAMING']);

      if (fetchError) throw fetchError;

      // Normalizar la lista
      const listaNormalizada = accesoriosLista
        .replace(/\u00A0/g, ' ')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n');

      const lineas = listaNormalizada.split('\n').filter(line => line.trim());
      const productosNuevos = [];
      const productosParaActualizar = [];
      const productosSinStockIds = [];
      const productosQuitadosDeListaIds = [];

      // Crear mapa de productos existentes
      const existingProductsMap = new Map(
        existingProducts.map(p => [p.name.toLowerCase(), p])
      );

      // Función para determinar categoría (mayúsculas, sin acentos para consistencia con App.js)
      // Por defecto para accesorios devuelve 'ACCESORIOS CELUS' (no 'ACCESORIOS')
      const determinarCategoria = (nombre) => {
        const nombreLower = nombre.toLowerCase();
        if (nombreLower.includes('iphone') || nombreLower.includes('airpods') || nombreLower.includes('airtag') || nombreLower.includes('ipad') || nombreLower.includes('macbok') || nombreLower.includes('watch') || nombreLower.includes('pencil') || nombreLower.includes('power adapter') || nombreLower.includes('magsafe')) return 'ECOSISTEMA APPLE';
        if (nombreLower.includes('nb ') || nombreLower.includes('notebook') || nombreLower.includes('lenovo') || nombreLower.includes('asus') || nombreLower.includes('dell') || nombreLower.includes('hp') || nombreLower.includes('msi')) return 'NOTEBOOKS';
        if (nombreLower.includes('tablet') || nombreLower.includes('tab ') || nombreLower.includes('quantum')) return 'TABLETS';
        if (nombreLower.includes('jbl') && (nombreLower.includes('xtreme') || nombreLower.includes('pulse') || nombreLower.includes('charge') || nombreLower.includes('flip') || nombreLower.includes('clip') || nombreLower.includes('go') || nombreLower.includes('wind'))) return 'AUDIO-PARLANTES';
        if (nombreLower.includes('auri') || nombreLower.includes('auriculares') || nombreLower.includes('ear') || nombreLower.includes('buds')) return 'AUDIO-AURIS';
        if (nombreLower.includes('cinema') || nombreLower.includes('barras')) return 'AUDIO-BARRAS DE SONIDO';
        if (nombreLower.includes('watch') || nombreLower.includes('amazfit') || nombreLower.includes('band') || nombreLower.includes('x-view')) return 'SMARTWATCHES';
        if (nombreLower.includes('echo') || nombreLower.includes('kindle') || nombreLower.includes('gopro') || nombreLower.includes('licuadora') || nombreLower.includes('pava') || nombreLower.includes('termo') || nombreLower.includes('balanza') || nombreLower.includes('scooter') || nombreLower.includes('tv box') || nombreLower.includes('robot')) return 'ELECTRONICA LIVIANA';
        if (nombreLower.includes('quest') || nombreLower.includes('ps') || nombreLower.includes('nintendo') || nombreLower.includes('xbox') || nombreLower.includes('control')) return 'CONSOLAS/GAMING';
        return 'ACCESORIOS CELUS';
      };

      // Set con nombres de la nueva lista
      const nombresEnNuevaLista = new Set();
      // Set para evitar duplicados dentro de la misma lista pegada
      const nombresVistosEnLista = new Set();

      let categoriaActual = 'Accesorios';
      let i = 0;
      
      while (i < lineas.length) {
        const linea = lineas[i].trim();
        
        if (!linea) {
          i++;
          continue;
        }

        // Detectar categoría con * asteriscos (única forma válida de especificar categoría)
        const esCategoriaAsteriscos = linea.startsWith('*') && linea.endsWith('*');
        if (esCategoriaAsteriscos) {
          categoriaActual = linea.replace(/\*/g, '').trim().toUpperCase();
          console.log('🏷️ Categoría detectada (asteriscos):', categoriaActual);
          i++;
          continue;
        }

        // NO detectar categorías sin asteriscos - usar la última categoría de asteriscos
        // Esto evita que nombres de productos se confundan con categorías

        // Procesar producto
        let nombre = '';
        let precioUSD = 0;
        let esSinStock = false;
        const tieneTab = linea.includes('\t');

        if (tieneTab) {
          // Formato con tab: Nombre[TAB]Precio
          const partes = linea.split('\t').map(item => item.trim());
          nombre = partes[0];
          const precioStr = partes[1] || '';
          console.log('📄 Línea con tab:', linea);
          
          if (nombre && precioStr) {
            esSinStock = precioStr.toLowerCase() === 'sin stock';
            if (!esSinStock) {
              const precioLimpio = precioStr.replace(/[$ \t,\u00A0]/g, '').trim();
              precioUSD = parseFloat(precioLimpio) || 0;
            }
          }
        } else {
          // Formato sin tab: Nombre en línea actual, precio en siguiente línea
          const tieneSignoPeso = linea.includes('$');
          esSinStock = linea.toLowerCase().trim() === 'sin stock';
          
          if (tieneSignoPeso || esSinStock) {
            // Esta línea es precio, línea anterior es nombre
            console.log('💰 Procesando línea precio:', linea);
            if (i > 0) {
              const lineaAnterior = lineas[i - 1];
              const esAnteriorPrecio = lineaAnterior.includes('$') || lineaAnterior.toLowerCase().trim() === 'sin stock';
              console.log('   Línea anterior:', lineaAnterior, '| Es precio?', esAnteriorPrecio);
              
              if (!esAnteriorPrecio) {
                nombre = lineaAnterior;
                if (!esSinStock) {
                  const precioLimpio = linea.replace(/[$ \t,\u00A0]/g, '').trim();
                  precioUSD = parseFloat(precioLimpio) || 0;
                }
              }
            }
          }
        }

        if (nombre) {
          nombresEnNuevaLista.add(nombre.toLowerCase());

          // Verificar si ya vimos este nombre en esta misma lista (evitar duplicados)
          if (nombresVistosEnLista.has(nombre.toLowerCase())) {
            console.log(`⚠️ Producto duplicado en la lista, ignorando: "${nombre}"`);
            i++;
            continue;
          }
          nombresVistosEnLista.add(nombre.toLowerCase());

          if (esSinStock) {
            const existingProduct = existingProductsMap.get(nombre.toLowerCase());
            if (existingProduct) {
              productosSinStockIds.push(existingProduct.id);
            }
          } else if (precioUSD > 0) {
            const existingProduct = existingProductsMap.get(nombre.toLowerCase());
            const categoria = categoriaActual;
            
            if (existingProduct) {
              // Producto existe - actualizar precio siempre
              productosParaActualizar.push({
                ...existingProduct,
                name: nombre,
                categoria: categoria,
                priceusd: precioUSD,
                price: precioUSD,
                pricears: Math.round(precioUSD * dolarValue),
                stock: true,
                visible: true,
                last_price_update: new Date().toISOString()
              });
            } else {
              // Producto nuevo - agregar a lista (sin id, se insertará por separado)
              productosNuevos.push({
                name: nombre,
                categoria: categoria,
                priceusd: precioUSD,
                price: precioUSD,
                pricears: Math.round(precioUSD * dolarValue),
                stock: true,
                visible: true,
                image: 'blank.jpg',
                margin_applied: 0,
                last_price_update: new Date().toISOString()
              });
            }
          }
        }

        i++;
      }

      // Productos que estaban en la BD pero ya no están en la nueva lista
      existingProducts.forEach(producto => {
        if (!nombresEnNuevaLista.has(producto.name.toLowerCase())) {
          productosQuitadosDeListaIds.push(producto.id);
        }
      });

      // Insertar productos nuevos (sin id para que se genere automáticamente)
      if (productosNuevos.length > 0) {
        const { error: insertError } = await supabase
          .from('products')
          .insert(productosNuevos);
        if (insertError) throw insertError;
      }

      // Actualizar productos existentes (ya tienen id)
      if (productosParaActualizar.length > 0) {
        const { error: updateError } = await supabase
          .from('products')
          .upsert(productosParaActualizar);
        if (updateError) throw updateError;
      }

      // Marcar productos sin stock como no visibles
      const todosSinStock = [...productosSinStockIds, ...productosQuitadosDeListaIds];
      if (todosSinStock.length > 0) {
        const { error: stockError } = await supabase
          .from('products')
          .update({ stock: false, visible: false })
          .in('id', todosSinStock);
        if (stockError) throw stockError;
      }

      // Mensaje resumen
      let mensajeResumen = '';
      if (productosNuevos.length > 0) {
        mensajeResumen += `\n✨ Productos nuevos agregados: ${productosNuevos.length}`;
      }
      if (productosParaActualizar.length > 0) {
        mensajeResumen += `\n🔄 Productos actualizados: ${productosParaActualizar.length}`;
      }
      if (todosSinStock.length > 0) {
        mensajeResumen += `\n❌ Productos marcados sin stock: ${todosSinStock.length}`;
      }

      setAccesoriosLista("");
      setMensajeAccesorios(`Lista procesada exitosamente.${mensajeResumen}`);
      
      console.log('📋 Resumen de productos procesados:');
      console.log('   Nuevos:', productosNuevos.map(p => `${p.name} (${p.categoria})`).join(', '));
      console.log('   Actualizados:', productosParaActualizar.map(p => `${p.name} (${p.categoria})`).join(', '));
    } catch (error) {
      console.error('Error:', error);
      setMensajeAccesorios(`Error al procesar la lista: ${error.message}`);
    } finally {
      setCargandoAccesorios(false);
    }
  };

  const [isMarginsModalOpen, setIsMarginsModalOpen] = useState(false);
  const [productosPendientesImagen, setProductosPendientesImagen] = useState([]);
  const [showPendingImagesModal, setShowPendingImagesModal] = useState(false);

  // Función reutilizable para cargar productos pendientes
  const loadPendingImages = async () => {
    try {
      const { data: products, error } = await supabase
        .from('products')
        .select('*')
        .or('image.is.null,image.eq.blank.jpg,image.eq.');

      if (error) throw error;

      const pendientes = products.map(p => ({
        id: p.id,
        nombre: p.name,
        categoria: p.categoria,
        imagePath: p.image || '',
        fechaCreacion: p.created_at || new Date().toISOString()
      }));

      setProductosPendientesImagen(pendientes);
    } catch (error) {
      console.error('Error al cargar productos pendientes:', error);
    }
  };

  // Cargar productos pendientes de imagen al iniciar
  useEffect(() => {
    loadPendingImages();
  }, []);

  // Actualizar lista de pendientes después de procesar listas
  useEffect(() => {
    if (mensajeCelulares.includes('exitosamente') || mensajeAccesorios.includes('exitosamente')) {
      loadPendingImages();
    }
  }, [mensajeCelulares, mensajeAccesorios]);

  const handleUploadPendingImage = async (productId, file) => {
    try {
      // Crear un nombre de archivo seguro (igual que ImageModal)
      const fileExt = file.name.split('.').pop();
      const fileName = `${productId}_${Date.now()}.${fileExt}`;

      // Subir archivo a Supabase Storage (igual que ImageModal)
      const { data, error } = await supabase.storage
        .from('images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw new Error('Error al subir la imagen: ' + error.message);
      }

      // Actualizar el producto con solo el nombre del archivo (igual que ImageModal)
      const { error: updateError } = await supabase
        .from('products')
        .update({ image: fileName })
        .eq('id', productId);

      if (updateError) {
        throw new Error('Error al actualizar el producto: ' + updateError.message);
      }

      // Actualizar la lista de productos pendientes inmediatamente
      setProductosPendientesImagen(prev => prev.filter(p => p.id !== productId));

      // Actualizar el estado local de productos (igual que handleImageUpdate)
      setAllProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, image: fileName } : p
      ));

      // Recargar la lista completa para asegurar sincronización
      setTimeout(() => {
        loadPendingImages();
      }, 500);

      // Mostrar mensaje de éxito
      console.log(`✅ Imagen subida exitosamente para: ${productId}`);
    } catch (error) {
      console.error('Error al subir imagen:', error);
      alert('Error al subir la imagen: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">LT Celulares - Panel</h1>
              <p className="text-gray-600">Bienvenido, Lucas</p>
            </div>
            <div className="flex items-center space-x-4">
              <a
                href="https://ltcelulares.store"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-blue-50 text-blue-600 font-medium hover:bg-blue-100 hover:text-blue-700 transition-all duration-300 hover:shadow-md active:bg-blue-200"
                title="Ver Tienda"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </a>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg bg-red-50 text-red-600 font-medium hover:bg-red-100 hover:text-red-700 transition-all duration-300 hover:shadow-md active:bg-red-200"
                title="Cerrar Sesión"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Casillero para editar el dólar */}
      {/* 1. Quitar el bloque superior de dólar (si existe) */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs y Dólar actual en la misma línea */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('ventas')}
              className={`px-6 py-2.5 rounded-full font-medium transition-all duration-300 transform hover:scale-105 ${activeTab === 'ventas'
                ? 'bg-white text-gray-900 shadow-xl'
                : 'bg-gray-100 text-gray-600 hover:bg-white hover:shadow-xl'
                }`}
            >
              Ventas
            </button>
            <button
              onClick={() => setActiveTab('historial')}
              className={`px-6 py-2.5 rounded-full font-medium transition-all duration-300 transform hover:scale-105 ${activeTab === 'historial'
                ? 'bg-white text-gray-900 shadow-xl'
                : 'bg-gray-100 text-gray-600 hover:bg-white hover:shadow-xl'
                }`}
            >
              Historial
            </button>
            <button
              onClick={() => setActiveTab('listas')}
              className={`px-6 py-2.5 rounded-full font-medium transition-all duration-300 transform hover:scale-105 ${activeTab === 'listas'
                ? 'bg-white text-gray-900 shadow-xl'
                : 'bg-gray-100 text-gray-600 hover:bg-white hover:shadow-xl'
                }`}
            >
              Listas
            </button>
            <button
              onClick={() => setActiveTab('productos')}
              className={`px-6 py-2.5 rounded-full font-medium transition-all duration-300 transform hover:scale-105 ${activeTab === 'productos'
                ? 'bg-white text-gray-900 shadow-xl'
                : 'bg-gray-100 text-gray-600 hover:bg-white hover:shadow-xl'
                }`}
            >
              Productos
            </button>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowPendingImagesModal(true)}
              className="flex items-center gap-2 px-6 py-2.5 bg-orange-500 text-white rounded-full font-medium hover:bg-orange-600 active:bg-orange-700 transition-colors shadow-lg hover:shadow-xl relative"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Subir Imágenes
              {productosPendientesImagen.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center font-bold">
                  {productosPendientesImagen.length}
                </span>
              )}
            </button>

            <button
              onClick={() => setIsMarginsModalOpen(true)}
              className="flex items-center gap-2 px-6 py-2.5 bg-purple-500 text-white rounded-full font-medium hover:bg-purple-600 active:bg-purple-700 transition-colors shadow-lg hover:shadow-xl"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Márgenes
            </button>

            <div
              ref={dolarButtonRef}
              className={`transition-all duration-300 rounded-full shadow p-0 flex items-center cursor-pointer group border border-green-300 px-6 py-2 min-h-[40px] 
                ${guardandoDolar ? 'scale-110 shadow-2xl bg-green-200' : 'scale-100 shadow bg-green-100 hover:bg-green-200'}`}
              style={{ minWidth: 0, height: 44, transition: 'all 0.3s cubic-bezier(.4,0,.2,1)' }}
              onClick={() => {
                if (!guardandoDolar) {
                  obtenerDolarSantander();
                }
              }}
            >
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold text-gray-600">Dólar actual:</span>
                <span className="text-lg font-mono text-green-700 select-none">${dolar}</span>
                {guardandoDolar ? (
                  <svg className="animate-spin h-4 w-4 text-green-600" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
              </div>
              {mensajeDolar && (
                <div className="absolute top-full left-0 mt-2 px-3 py-1 bg-green-100 text-green-700 text-sm rounded-lg shadow-lg whitespace-nowrap z-10">
                  {mensajeDolar}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contenido de la sección Listas */}
        {activeTab === 'listas' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Lista de Celulares */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold mb-4">Lista de Celulares</h3>
              <div className="space-y-4">
                <textarea
                  value={celularesLista}
                  onChange={(e) => setCelularesLista(e.target.value)}
                  placeholder="Pega aquí la lista de celulares..."
                  className="w-full h-64 p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none font-mono"
                  disabled={cargandoCelulares}
                />
                <div className="flex justify-end">
                  <button
                    onClick={procesarListaCelulares}
                    disabled={cargandoCelulares || !celularesLista.trim()}
                    className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {cargandoCelulares ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Procesando...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Procesar Lista
                      </>
                    )}
                  </button>
                </div>
                {mensajeCelulares && (
                  <div className={`mt-4 p-4 rounded-lg ${mensajeCelulares.includes('Error') ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'} whitespace-pre-line`}>
                    {mensajeCelulares}
                  </div>
                )}
              </div>
            </div>

            {/* Lista de Accesorios */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold mb-4">Lista de Accesorios</h3>
              <div className="space-y-4">
                <textarea
                  value={accesoriosLista}
                  onChange={(e) => setAccesoriosLista(e.target.value)}
                  placeholder="Pega aquí la lista de accesorios..."
                  className="w-full h-64 p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none font-mono"
                  disabled={cargandoAccesorios}
                />
                <div className="flex justify-end">
                  <button
                    onClick={procesarListaAccesorios}
                    disabled={cargandoAccesorios || !accesoriosLista.trim()}
                    className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {cargandoAccesorios ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Procesando...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Procesar Lista
                      </>
                    )}
                  </button>
                </div>
                {mensajeAccesorios && (
                  <div className={`mt-4 p-4 rounded-lg ${mensajeAccesorios.includes('Error') ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'} whitespace-pre-line`}>
                    {mensajeAccesorios}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sección de productos disponibles */}
        {activeTab === 'productos' && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {showVisibleProducts ? 'Productos visibles' : 'Productos ocultos'} en la tienda
              </h3>
              <button
                onClick={() => setShowVisibleProducts(!showVisibleProducts)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {showVisibleProducts ? 'Ver ocultos' : 'Ver visibles'}
              </button>
            </div>
            {/* Filtro de búsqueda */}
            <input
              type="text"
              placeholder="Buscar producto..."
              value={productSearch}
              onChange={e => setProductSearch(e.target.value)}
              className="mb-4 px-4 py-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {allProducts && allProducts.length > 0 && (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Visible</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Imágenes</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Precio USD</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {allProducts
                    .filter(product => {
                      const matchesSearch = product.name.toLowerCase().includes(productSearch.toLowerCase());
                      const matchesVisibility = showVisibleProducts ? (product.visible !== false) : (product.visible === false);
                      // Incluir productos que están en proceso de desvanecimiento
                      const isFading = fadingProducts.has(product.id);
                      return matchesSearch && (matchesVisibility || isFading);
                    })
                    .map(product => (
                      <tr
                        key={product.id}
                        className={`transition-all duration-300 ease-in-out ${fadingProducts.has(product.id)
                          ? 'opacity-20 bg-gray-100'
                          : product.visible === false
                            ? 'opacity-50 bg-gray-50'
                            : 'opacity-100 bg-white'
                          }`}
                      >
                        <td className="px-6 py-2 text-center align-middle h-full">
                          <div className="flex items-center justify-center h-full min-h-[40px] w-full">
                            <input
                              type="checkbox"
                              checked={product.visible !== false}
                              onChange={() => handleToggleProductVisible(product.id)}
                              className={`w-4 h-4 rounded focus:ring-2 transition-all duration-300 ${fadingProducts.has(product.id)
                                ? 'text-gray-300 bg-gray-100 border-gray-200 focus:ring-gray-300'
                                : product.visible === false
                                  ? 'text-gray-400 bg-gray-200 border-gray-300 focus:ring-gray-400'
                                  : 'text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500'
                                }`}
                            />
                          </div>
                        </td>
                        <td className={`px-6 py-2 transition-colors duration-300 ${fadingProducts.has(product.id)
                          ? 'text-gray-300'
                          : product.visible === false
                            ? 'text-gray-400'
                            : 'text-gray-900'
                          }`}>{product.name}</td>
                        <td className="px-6 py-2 text-center align-middle">
                          <div
                            className="relative group inline-block"
                            onMouseEnter={() => {
                              console.log('🔍 Hover sobre producto:', {
                                name: product.name,
                                image: product.image,
                                categoria: product.categoria,
                                hasImage: product.image && product.image !== 'blank.jpg' && product.image !== '',
                                imagePath: `/images/${product.image}`,
                                fullProduct: product
                              });
                            }}
                          >
                            <button
                              onClick={() => handleOpenImageModal(product)}
                              className={`p-2 rounded-lg transition-all duration-200 ${
                                product.image && product.image !== 'blank.jpg' && product.image !== ''
                                  ? 'text-violet-600 hover:text-violet-700 hover:bg-violet-50'
                                  : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                              }`}
                              title={product.image && product.image !== 'blank.jpg' && product.image !== '' ? 'Gestionar imagen' : 'Sin imagen'}
                            >
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </button>

                            <button
                              onClick={() => handleOpenMagicWandModal(product)}
                              className={`p-2 rounded-lg transition-all duration-200 ${
                                product.image && product.image !== 'blank.jpg' && product.image !== ''
                                  ? 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50'
                                  : 'text-red-500 hover:text-red-700 hover:bg-red-50'
                              }`}
                              title="Buscar imagen en internet"
                            >
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                              </svg>
                            </button>

                            {/* Tooltip con previsualización de imagen */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-all duration-300 delay-100 pointer-events-none z-[9999]">
                              <div className="bg-white border border-gray-200 rounded-lg shadow-xl p-3 max-w-sm">
                                <div className="w-40 h-40 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                                  {(product.image && product.image !== 'blank.jpg' && product.image !== '') ? (
                                    <img
                                      src={`https://xsuruuynxrxrarvalzjb.supabase.co/storage/v1/object/public/images/${product.image}`}
                                      alt={product.name}
                                      className="w-full h-full object-contain rounded-lg"
                                      onError={(e) => {
                                        console.log('❌ Error cargando imagen:', e.target.src);
                                        // Si falla, mostrar placeholder
                                        e.target.style.display = 'none';
                                        const container = e.target.parentElement;
                                        if (container) {
                                          const placeholder = container.querySelector('.placeholder-icon');
                                          if (placeholder) {
                                            placeholder.style.display = 'flex';
                                          }
                                        }
                                      }}
                                      onLoad={() => {
                                        console.log('✅ Imagen cargada exitosamente:', product.name, product.image);
                                      }}
                                    />
                                  ) : (
                                    <div className="flex items-center justify-center w-full h-full placeholder-icon" style={{ display: 'flex' }}>
                                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                    </div>
                                  )}
                                  {/* Icono de placeholder - siempre presente pero oculto cuando hay imagen */}
                                  <div className="flex items-center justify-center w-full h-full placeholder-icon" style={{ display: 'none' }}>
                                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-600 mt-1 text-center truncate">{product.name}</p>
                              </div>
                              {/* Flecha del tooltip */}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-2 text-center align-middle relative">
                          {editingPriceId === product.id ? (
                            <div className="absolute right-6 top-1/2 transform -translate-y-1/2 flex items-center gap-2 bg-white border border-gray-200 rounded-full px-2 py-1 shadow-lg z-10">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editingPriceValue}
                                onChange={e => setEditingPriceValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    handleSavePriceUSD(product.id);
                                  } else if (e.key === 'Escape') {
                                    setEditingPriceId(null);
                                    setEditingPriceValue("");
                                  }
                                }}
                                className="w-20 px-2 py-1 border-0 focus:outline-none focus:ring-0 text-center bg-transparent"
                                autoFocus
                              />
                              <button
                                className="px-2 py-1 bg-green-500 text-white rounded-full hover:bg-green-600 text-xs"
                                onClick={() => handleSavePriceUSD(product.id)}
                              >
                                ✓
                              </button>
                              <button
                                className="px-2 py-1 bg-red-500 text-white rounded-full hover:bg-red-600 text-xs"
                                onClick={() => {
                                  setEditingPriceId(null);
                                  setEditingPriceValue("");
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setEditingPriceId(product.id);
                                setEditingPriceValue(product.price ?? "");
                              }}
                              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-all duration-200 font-mono text-gray-700 hover:text-gray-900 min-w-[80px] shadow-sm hover:shadow-md"
                              title="Hacer clic para editar precio"
                            >
                              {product.price ? `$${product.price}` : '-'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Estadísticas */}
        {activeTab !== 'productos' && activeTab !== 'listas' && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 w-full">
            <div className="bg-white rounded-lg shadow-lg p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:bg-gray-50">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Órdenes</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:bg-gray-50">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M12 7c1.657 0 3 .895 3 2s-1.343 2-3 2m0 0c-1.657 0-3-.895-3-2s1.343-2 3-2" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Facturación Total</p>
                  <p className="text-2xl font-bold text-gray-900">${stats.totalRevenue.toLocaleString('es-AR')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:bg-gray-50">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Ganancia NETA</p>
                  <p className="text-2xl font-bold text-gray-900">${stats.netProfit.toLocaleString('es-AR')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:bg-gray-50">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Unidades Vendidas</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalUnitsSold}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista de órdenes */}
        {activeTab !== 'productos' && activeTab !== 'listas' && (
          <div className="bg-white rounded-lg shadow-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <h3 className="text-lg font-medium text-gray-900">
                {activeTab === 'ventas' ? 'Órdenes Recientes' : 'Historial de Órdenes'}
              </h3>
              {/* Filtro de cliente */}
              <input
                type="text"
                placeholder="Buscar cliente..."
                value={orderClientSearch}
                onChange={e => setOrderClientSearch(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {/* Gráfico de productos más vendidos (solo en historial) */}
            {activeTab === 'historial' && (
              <div className="bg-white rounded-lg shadow-lg mb-8 p-4">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Productos Más Vendidos</h3>
                </div>
                <div className={isMobile ? 'p-2 flex flex-col items-center' : 'p-2 flex flex-col items-center md:flex-row md:items-center md:justify-start md:gap-2'} style={{ minHeight: 40 }}>
                  {stats.topProducts.length > 0 ? (
                    <>
                      {/* Leyenda en columna a la izquierda en escritorio */}
                      {!isMobile && (
                        <div className="flex flex-col items-start gap-3 min-w-[140px]">
                          {stats.topProducts.map((entry, index) => (
                            <div key={entry.name} className="flex items-center text-base" style={{ color: COLORS[index % COLORS.length] }}>
                              <span className="inline-block w-4 h-4 rounded-full mr-2" style={{ background: COLORS[index % COLORS.length] }}></span>
                              <span>{entry.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className={isMobile ? 'w-full' : ''}>
                        <div className="flex items-center">
                          <ResponsiveContainer width={isMobile ? '100%' : 320} height={isMobile ? 220 : 320}>
                            <PieChart>
                              <Pie
                                data={stats.topProducts}
                                dataKey="quantity"
                                nameKey="name"
                                cx="50%"
                                cy="45%"
                                outerRadius={isMobile ? 90 : 110}
                                innerRadius={isMobile ? 45 : 50}
                                label={renderCustomizedLabel}
                                labelLine={false}
                              >
                                {stats.topProducts.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value, name, props) => [`${value} unidades`, 'Ventas']} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No hay ventas registradas</p>
                  )}
                  {/* Leyenda en columna para móviles */}
                  {isMobile && stats.topProducts.length > 0 && (
                    <div className="mt-4 w-full flex flex-row flex-wrap items-center gap-x-4 gap-y-2 justify-center">
                      {stats.topProducts.map((entry, index) => (
                        <div key={entry.name} className="flex items-center text-xs" style={{ color: COLORS[index % COLORS.length] }}>
                          <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ background: COLORS[index % COLORS.length] }}></span>
                          <span>{entry.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tabla de órdenes */}
            <div className="overflow-x-auto">
              {filteredOrders.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Productos</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contacto</th>
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOrders
                      .filter(order => {
                        const cliente = `${order.customer?.name || ''} ${order.customer?.lastName || ''}`.toLowerCase();
                        return cliente.includes(orderClientSearch.toLowerCase());
                      })
                      .map((order, index) => {
                        const isPaid = order.status === 'paid';
                        const isArchived = order.archived;
                        return (
                          <tr
                            key={order.id}
                            className={`${isPaid ? 'bg-green-100' : ''} ${isArchived ? 'bg-gray-100' : ''} hover:bg-opacity-80 transition-colors`}
                            style={{ cursor: isArchived ? 'default' : 'pointer' }}
                            onClick={e => {
                              if (e.target.closest('button') || isArchived) return;
                              handleTogglePaid(order.id);
                            }}
                          >
                            <td className="px-6 py-4">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {order.customer.name} {order.customer.lastName}
                                </div>
                                <div className="text-sm text-gray-500">DNI: {order.customer.dni}</div>
                                <div className="text-xs text-gray-400 mt-1">ID Orden: {order.id}</div>
                                {isPaid && !isArchived && (
                                  <div className="mt-1 text-xs font-bold text-green-600 uppercase">PAGADO</div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 align-middle">
                              <div className="flex flex-col justify-center h-full min-h-[56px]">
                                {isMobile ? (
                                  <div>
                                    <button
                                      className="px-3 py-1 bg-gray-200 rounded hover:bg-blue-500 hover:text-white text-xs font-semibold mb-2"
                                      onClick={e => {
                                        e.stopPropagation();
                                        setProductsModalOrder(order);
                                        setShowProductsModal(true);
                                      }}
                                    >
                                      Ver productos vendidos
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex flex-col gap-1">
                                    {order.items.map((item, itemIndex) => (
                                      <div key={itemIndex} className="flex items-center min-h-[28px] justify-between gap-2 text-sm text-gray-700">
                                        <span>
                                          {item.name} - {item.selectedColor || 'Sin color'} (x{item.quantity})
                                          {item.priceusd && (
                                            <div className="text-xs text-green-600 font-semibold mt-1">
                                              U$D {item.priceusd}
                                            </div>
                                          )}
                                        </span>
                                        <button
                                          className={
                                            `ml-2 px-2 py-1 text-xs rounded font-semibold focus:outline-none ` +
                                            (item.serialNumber ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-200 hover:bg-blue-500 hover:text-white')
                                          }
                                          title="Ver/Editar N° de serie"
                                          onClick={e => {
                                            e.stopPropagation();
                                            setSerialEdit({ orderId: order.id, itemIndex, value: item.serialNumber || '', viewOnly: true });
                                            setShowSerialModal(true);
                                          }}
                                        >
                                          S/N
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              ${order.total.toLocaleString('es-AR')}
                              <div className="text-xs text-green-600 font-semibold mt-1">
                                Ganancia: ${calcularGananciaOrden(order).toLocaleString('es-AR')}
                              </div>
                              <div className="text-xs text-red-600 font-semibold mt-1">
                                Valor USD: {order.dolarusado || order.dolarUsado || order.dolar_usado || order.dolar || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(order.created_at)}  {/* <-- Cambiado de date a created_at */}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                <div>{order.customer.phone}</div>
                                <div className="text-gray-500 text-xs">{order.customer.address}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center justify-end gap-2">
                                {activeTab === 'ventas' && isPaid && !isArchived && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const { error } = await supabase
                                        .from('orders')
                                        .update({ archived: true })
                                        .eq('id', order.id);

                                      if (error) {
                                        console.error('Error al archivar:', error);
                                        alert('Error al archivar la orden');
                                        return;
                                      }

                                      setOrders(prev => prev.map(o =>
                                        o.id === order.id ? { ...o, archived: true } : o
                                      ));
                                    }}
                                    className="text-blue-500 hover:text-blue-700 w-8 h-8 rounded-full transition-colors transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-opacity-50 inline-flex items-center justify-center"
                                    title="Archivar orden"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                    </svg>
                                  </button>
                                )}
                                {activeTab === 'historial' && isArchived && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const { error } = await supabase
                                        .from('orders')
                                        .update({ archived: false })
                                        .eq('id', order.id);

                                      if (error) {
                                        console.error('Error al desarchivar:', error);
                                        alert('Error al desarchivar la orden');
                                        return;
                                      }

                                      setOrders(prev => prev.map(o =>
                                        o.id === order.id ? { ...o, archived: false } : o
                                      ));
                                    }}
                                    className="text-blue-500 hover:text-blue-700 w-8 h-8 rounded-full transition-colors transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-opacity-50 inline-flex items-center justify-center"
                                    title="Desarchivar orden"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                    </svg>
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteOrder(order.id);
                                  }}
                                  className="text-red-500 hover:text-red-700 w-8 h-8 rounded-full transition-colors transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-opacity-50 inline-flex items-center justify-center"
                                  title="Eliminar orden"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No hay órdenes</h3>
                  <p className="mt-1 text-sm text-gray-500">Las órdenes aparecerán aquí cuando los clientes realicen compras.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal para editar/agregar número de serie */}
      {showSerialModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg shadow-lg p-6 min-w-[320px] max-w-[90vw]">
            <div className="mb-6 text-center text-lg min-h-[32px]">
              <span className="font-semibold">Número de Serie:</span>
              <span className="ml-2 font-mono text-gray-700">{serialEdit.value ? serialEdit.value : <span className="text-gray-400">— Sin número de serie —</span>}</span>
            </div>
            <div className="flex justify-between gap-2">
              {serialEdit.viewOnly ? (
                <>
                  <button
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                    onClick={() => setSerialEdit(edit => ({ ...edit, viewOnly: false }))}
                  >
                    Editar
                  </button>
                  <button
                    className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                    onClick={() => setShowSerialModal(false)}
                  >
                    Cerrar
                  </button>
                </>
              ) : (
                <>
                  <input
                    className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    type="text"
                    value={serialEdit.value}
                    onChange={e => setSerialEdit(edit => ({ ...edit, value: e.target.value }))}
                    placeholder="Ingrese el número de serie"
                    autoFocus
                  />
                  <div className="flex justify-between gap-2">
                    <button
                      className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                      onClick={handleSaveSerial}
                    >
                      Guardar
                    </button>
                    <button
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                      onClick={() => setSerialEdit(edit => ({ ...edit, viewOnly: true }))}
                    >
                      Cancelar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de productos vendidos para móvil */}
      {isMobile && showProductsModal && productsModalOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 min-w-[320px] max-w-[90vw]">
            <h2 className="text-lg font-semibold mb-4 text-center">Productos Vendidos</h2>
            <div className="flex flex-col gap-2 mb-4">
              {productsModalOrder.items.map((item, itemIndex) => (
                <div key={itemIndex} className="flex items-center min-h-[28px] justify-between gap-2 text-sm text-gray-700 border-b last:border-b-0 py-1">
                  <span>{item.name} - {item.selectedColor || 'Sin color'} (x{item.quantity})</span>
                  <button
                    className={
                      `ml-2 px-2 py-1 text-xs rounded font-semibold focus:outline-none ` +
                      (item.serialNumber ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-200 hover:bg-blue-500 hover:text-white')
                    }
                    title="Ver/Editar N° de serie"
                    onClick={e => {
                      e.stopPropagation();
                      setSerialEdit({ orderId: productsModalOrder.id, itemIndex, value: item.serialNumber || '', viewOnly: true });
                      setShowSerialModal(true);
                    }}
                  >
                    S/N
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-center">
              <button
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => setShowProductsModal(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <MarginsModal
        isOpen={isMarginsModalOpen}
        onClose={() => {
          setIsMarginsModalOpen(false);
          // Recargar productos para reflejar los nuevos precios calculados
          if (activeTab === 'productos') {
            const fetchProducts = async () => {
              const { data, error } = await supabase
                .from('products')
                .select('*')
                .order('name', { ascending: true });
              if (!error && data) {
                setAllProducts(data);
              }
            };
            fetchProducts();
          }
        }}
      />

      <PendingImagesModal
        isOpen={showPendingImagesModal}
        onClose={() => {
          setShowPendingImagesModal(false);
          // Recargar la lista de productos pendientes al cerrar el modal
          loadPendingImages();
        }}
        productosPendientes={productosPendientesImagen}
        onImageUpload={handleUploadPendingImage}
        onClearAll={async () => {
          // Actualizar todos los productos pendientes con una imagen por defecto
          const ids = productosPendientesImagen.map(p => p.id);
          if (ids.length > 0) {
            const { error } = await supabase
              .from('products')
              .update({ image: 'no-image.jpg' })
              .in('id', ids);

            if (error) {
              console.error('Error al limpiar productos pendientes:', error);
              alert('Error al limpiar la lista');
              return;
            }

            // Limpiar la lista local
            setProductosPendientesImagen([]);

            // Actualizar el estado local de productos
            setAllProducts(prev => prev.map(p =>
              ids.includes(p.id) ? { ...p, image: 'no-image.jpg' } : p
            ));
          }
        }}
        onRemoveProduct={(id) => {
          setProductosPendientesImagen(prev => prev.filter(p => p.id !== id));
        }}
      />

      <ImageModal
        isOpen={showImageModal}
        onClose={handleCloseImageModal}
        product={selectedProductForImage}
        onImageUpdate={handleImageUpdate}
      />

      {notification && (
        <div className={`fixed top-4 left-4 z-[100] px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          notification.type === 'success' 
            ? 'bg-green-500 text-white' 
            : 'bg-red-500 text-white'
        }`}>
          {notification.type === 'success' ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className="font-medium">{notification.message}</span>
        </div>
      )}

      <MagicWandModal
        isOpen={showMagicWandModal}
        onClose={handleCloseMagicWandModal}
        product={selectedProductForMagicWand}
        onImageUpdate={handleImageUpdate}
        onNotify={showNotification}
      />



      {/* Al final del archivo, agregar los estilos globales para el checkbox custom */}
      <style>{`
      .checkbox-wrapper-18 .round {
        position: relative;
      }
      .checkbox-wrapper-18 .round label {
        background-color: #fff;
        border: 1px solid #ccc;
        border-radius: 50%;
        cursor: pointer;
        height: 28px;
        width: 28px;
        display: block;
      }
      .checkbox-wrapper-18 .round label:after {
        border: 2px solid #fff;
        border-top: none;
        border-right: none;
        content: "";
        height: 6px;
        left: 8px;
        opacity: 0;
        position: absolute;
        top: 9px;
        transform: rotate(-45deg);
        width: 12px;
      }
      .checkbox-wrapper-18 .round input[type="checkbox"] {
        visibility: hidden;
        display: none;
        opacity: 0;
      }
      .checkbox-wrapper-18 .round input[type="checkbox"]:checked + label {
        background-color: #66bb6a;
        border-color: #66bb6a;
      }
      .checkbox-wrapper-18 .round input[type="checkbox"]:checked + label:after {
        opacity: 1;
      }
      .hide-number-arrows::-webkit-outer-spin-button,
      .hide-number-arrows::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      .hide-number-arrows {
        -moz-appearance: textfield;
      }
      `}</style>
    </div>
  );
};

export default AdminDashboard; 